import React, { Fragment, useMemo, type FC } from "react";
import { Empty } from "./Empty";
import ColumnText from "../molecules/ColumnText";
import { Branded } from "./Branded";
import PaginationUI from "../atoms/PaginationUI";
import { useTranslation } from "react-i18next";
import { NutritionPlan } from "@/app/nutrition/plan";

export type NutritionSearchProps = {
  isLoading?: boolean;
  pagination?: any;
  onOverview: (plan: NutritionPlan) => void;
  onDelete: (plan: NutritionPlan) => void;
  onEdit: (plan: NutritionPlan) => void;
  onAssign: (plan: NutritionPlan) => void;
  onDuplicate: (plan: NutritionPlan) => void;
};

export const NutritionSearch: FC<NutritionSearchProps> = ({
  pagination,
  isLoading,
  onOverview,
  onDelete,
  onEdit,
  onAssign,
  onDuplicate,
}) => {
  const { t } = useTranslation();
  const actions = useMemo(
    () => [
      {
        id: 1,
        title: t("contexts.nutrition.view_plan"),
        description: t("contexts.nutrition.view_plan_description"),
        function: (plan: NutritionPlan) => {
          onOverview(plan);
        },
      },
      {
        id: 2,
        title: t("contexts.nutrition.duplicate_plan"),
        description: t("contexts.nutrition.duplicate_plan_description"),
        function: (plan: NutritionPlan) => {
          onDuplicate(plan);
        },
      },
      {
        id: 3,
        title: t("contexts.nutrition.assign_plan"),
        description: t("contexts.nutrition.assign_plan_description"),
        function: async (plan: NutritionPlan) => {
          onAssign(plan);
        },
      },
      {
        id: 4,
        title: t("contexts.nutrition.edit_plan"),
        description: t("contexts.nutrition.edit_plan_description"),
        function: (plan: NutritionPlan) => onEdit(plan),
      },
      {
        id: 5,
        title: t("contexts.nutrition.delete_plan"),
        description: t("contexts.nutrition.delete_plan_description"),
        function: (plan: NutritionPlan) => {
          onDelete(plan);
        },
      },
    ],
    []
  );

  return (
    <div className="mt-5">
      <div className="px-2 sm:px-3 lg:px-6">
        <div className="flow-root">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full px-7 py-2 align-middle">
              {pagination?.data?.length === 0 ? (
                <Empty texto="no hay datos" />
              ) : (
                <Fragment>
                  <table className="min-w-full divide-y divide-gray-300 border-t border-gray-300">
                    <thead>
                      <tr>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("contexts.shared.name")}
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("contexts.shared.objective")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {pagination?.data?.map((plan) => (
                        <tr key={plan.id}>
                          <ColumnText title={plan.plan} />
                          <ColumnText
                            title={t(
                              `contexts.training.objective_${plan.nutritionObjective}`
                            )}
                          />
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            <Branded
                              title={t("contexts.shared.actions")}
                              publishingOptions={actions.map((option) => ({
                                ...option,
                                function: () => option.function(plan),
                              }))}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Fragment>
              )}
            </div>
          </div>
        </div>
      </div>

      <PaginationUI
        currentPage={pagination?.currentPage}
        totalPages={pagination?.lastPage}
        onPageChange={pagination?.onPageChange}
        pageSize={pagination?.perPage}
        totalResults={pagination?.total}
      />
    </div>
  );
};
